import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { getConfig } from '../config.service';

interface TranslationResponse {
	translations: { key: string; value: string }[];
}

const STORAGE_KEY = 'skomap_language';
const DEFAULT_LANGUAGE = 'cs';

@Injectable({ providedIn: 'root' })
export class TranslationService {
	private http = inject(HttpClient);

	// Per-language cache (like legacy app)
	private cache: Record<string, Map<string, string>> = {};

	// Current active translations and language as signals so that every
	// template reading them (via TranslatePipe) re-renders immediately on
	// language switch
	private translationsSig = signal<Map<string, string>>(new Map());
	private currentLanguageSig = signal(DEFAULT_LANGUAGE);
	private loadedSig = signal(false);

	/** Current language code as a readonly signal. */
	readonly language = this.currentLanguageSig.asReadonly();

	/** Whether translations have been loaded (or loading failed and keys are shown). */
	readonly loaded = this.loadedSig.asReadonly();

	/**
	 * Load translations for a culture. Uses cache if available.
	 */
	async loadTranslations(culture: string = DEFAULT_LANGUAGE): Promise<void> {
		// Check cache first
		if (this.cache[culture]) {
			console.log(`[TranslationService] Using cached translations for ${culture}`);
			this.translationsSig.set(this.cache[culture]);
			this.currentLanguageSig.set(culture);
			this.loadedSig.set(true);
			return;
		}

		try {
			const url = `${getConfig().api.middlewareUrl}/api/translations/culture/${culture}/namespace/Web`;
			const response = await firstValueFrom(
				this.http.get<TranslationResponse>(url)
			);

			// Create new Map for this culture
			const cultureTranslations = new Map<string, string>();
			response.translations.forEach(t =>
				cultureTranslations.set(t.key, t.value)
			);

			// Store in cache
			this.cache[culture] = cultureTranslations;

			// Set as current
			this.translationsSig.set(cultureTranslations);
			this.currentLanguageSig.set(culture);
			this.loadedSig.set(true);

			console.log(`[TranslationService] Loaded ${cultureTranslations.size} translations for ${culture}`);
		} catch (error) {
			console.error('[TranslationService] Failed to load translations:', error);
			// No fallback - keys will be shown instead (same as legacy app)
			this.loadedSig.set(true);
		}
	}

	/**
	 * Switch to a different language. Uses cache if available, fetches if not.
	 * Persists the preference to localStorage.
	 */
	async switchLanguage(culture: string): Promise<void> {
		if (culture === this.currentLanguageSig()) {
			return;
		}

		await this.loadTranslations(culture);
		localStorage.setItem(STORAGE_KEY, culture);
	}

	/**
	 * Get stored language preference from localStorage.
	 */
	getStoredLanguage(): string {
		return localStorage.getItem(STORAGE_KEY) || DEFAULT_LANGUAGE;
	}

	/**
	 * Translate a key to the current language. Reading this inside a template
	 * (or TranslatePipe) registers a signal dependency, so the view refreshes
	 * automatically when the language changes.
	 *
	 * A missing key yields the fallback text when given, otherwise the key
	 * itself (same as the legacy app), so a missing translation stays visible.
	 * Note: because the key is returned, `translate(key) || 'text'` never
	 * falls back; pass the text as the second argument instead.
	 */
	translate(key: string, fallback?: string): string {
		return this.translationsSig().get(key) ?? fallback ?? key;
	}

	/**
	 * Get the current language code.
	 */
	getCurrentLanguage(): string {
		return this.currentLanguageSig();
	}

	/**
	 * Clear the cache for a specific culture or all cultures.
	 */
	clearCache(culture?: string): void {
		if (culture) {
			delete this.cache[culture];
		} else {
			this.cache = {};
		}
	}

}
